<template>

  <vx-card :subtitle="subjectCompute.nameLocal"
           :title="subjectCompute.code"
           class="mb-base"
           subtitle-color="warning"
           title-color="primary">

    <p>{{ subjectCompute.description }}</p>

    <template slot="actions">
      <vs-chip transparent color="primary">
        <vs-avatar class="bg-primary cursor-default"
                   :text="String(subjectCompute.creditCalGrade)"/>
        {{ $t('student-enroll.credit') }}
      </vs-chip>
    </template>

    <div class="vx-row items-center">
      <div class="vx-col w-full">
        <h5>{{ teacherCompute | teacherName }}</h5>
      </div>
      <vs-collapse class="p-0 w-full">

        <vs-collapse-item class="mt-0">

          <div slot="header">
            {{ $t('course-outline.upload-course-picture') }}
          </div>
          <div class="vx-row mb-2">
            <div class="vx-col sm:w-3/4">
              <img width="640"
                   height="360"
                   :src="horizontalImageUrl"
                   style="display: block; margin-left: auto; margin-right: auto;"/>
            </div>
            <div class="vx-col sm:w-1/4">
              <img width="200"
                   height="356"
                   :src="verticalImageUrl"
                   style="display: block; margin-left: auto; margin-right: auto;"/>
            </div>
          </div>

          <div class="vx-row text-center mb-2">
            <div class="vx-col sm:w-3/4">
              <input type="file" class="hidden" ref="update_horizontal_picture_input"
                     @change="uploadHorizontalImage"
                     accept="image/*">
              <vs-button color="primary" icon-pack="feather" icon="icon-upload"
                         class="w-full"
                         @click="$refs.update_horizontal_picture_input.click()">
                {{ $t('course-outline.upload-course-picture') }}
              </vs-button>
              <p>{{ $t('course-outline.horizon-image-size') }}</p>
              <p>{{ $t('course-outline.thumbnail-type') }}</p>
            </div>
            <div class="vx-col sm:w-1/4">
              <input type="file" class="hidden" ref="update_vertical_picture_input"
                     @change="uploadVerticalImage"
                     accept="image/*">
              <vs-button color="primary" icon-pack="feather" icon="icon-upload"
                         class="w-full"
                         @click="$refs.update_vertical_picture_input.click()">
                {{ $t('course-outline.upload-course-picture-vertical') }}
              </vs-button>
              <p>{{ $t('course-outline.vertical-image-size') }}</p>
              <p>{{ $t('course-outline.thumbnail-type') }}</p>
            </div>
          </div>

        </vs-collapse-item>

      </vs-collapse>
    </div>

  </vx-card>

</template>

<script>
  import _store from '@/store';
  import {config, getPhotoUrl} from "@/utils/upload";
  import CourseOutline from '@/store/modules/courses/course-outline/index';
  import Upload from '@/store/modules/upload/index';
  import {createHelpers} from 'vuex-map-fields';
  import {createNamespacedHelpers} from "vuex";
  import {state} from "../data";
  import {notificationType, showLocalNotification} from "@/utils/notification";

  if (!_store.state.Upload) _store.state.registerModule(`Upload`, Upload);
  if (!state.store.CourseOutline) state.registerModule(`CourseOutline`, CourseOutline);

  const {
    mapActions: mapUploadActions,
  } = createNamespacedHelpers(`Upload`);
  const {
    mapActions: mapCourseOutlineActions,
  } = createNamespacedHelpers(`CourseOutline`);


  const {
    mapFields: mapUploadFields,
  } = createHelpers({
    getterType: `Upload/getField`,
    mutationType: `Upload/updateField`,
  });
  const {
    mapFields: mapCourseOutlineFields,
  } = createHelpers({
    getterType: `CourseOutline/getField`,
    mutationType: `CourseOutline/updateField`,
  });

  export default {
    props: {
      courseOutline: Object,
    },
    data() {
      return {
        horizontalFileUpload: null,
        verticalFileUpload: null,
        horizontalImage: {
          width: 1920,
          height: 1080,
          size: 1000000 // 1000000 b = 1 mb
        },
        verticalImage: {
          width: 200,
          height: 356,
          size: 500000 // 500000 b = 500 kb
        },
      }
    },
    computed: {
      ...mapUploadFields({
        uploadProcessing: 'processing',
      }),
      ...mapCourseOutlineFields({
        courseOutlineProcessing: 'processing'
        , courseOutlineSuccess: 'success', courseOutlineError: 'error'
      }),
      subjectCompute() {
        return this.courseOutline ? this.courseOutline.subject : {}
      },
      teacherCompute() {
        return this.courseOutline ? this.courseOutline.teacher : {}
      },
      horizontalImageUrl() {
        if (this.horizontalFileUpload) {
          return URL.createObjectURL(this.horizontalFileUpload)
        } else if (!this.horizontalFileUpload && this.courseOutline && this.courseOutline.picturePath) {
          return getPhotoUrl(this.courseOutline.picturePath);
        } else {
          return config.url.noPhotoImage;
        }
      },
      verticalImageUrl() {
        if (this.verticalFileUpload) {
          return URL.createObjectURL(this.verticalFileUpload)
        } else if (!this.verticalFileUpload && this.courseOutline && this.courseOutline.verticalPicturePath) {
          return getPhotoUrl(this.courseOutline.verticalPicturePath);
        } else {
          return config.url.noPhotoImage;
        }
      },
    },
    methods: {
      ...mapUploadActions({
        uploadAvatar: 'uploadAvatar',
      }),
      ...mapCourseOutlineActions({
        updateCourseOutline: 'updateCourseOutline',
      }),
      async uploadHorizontalImage(e) {
        await this.uploadImage(e, 'horizontal')
      },
      async uploadVerticalImage(e) {
        await this.uploadImage(e, 'vertical')
      },
      async uploadImage(e, type){
        const imageType = type === 'vertical' ? this.verticalImage:this.horizontalImage

        if (e.target.files && e.target.files.length > 0) {
          let file = e.target.files[0];
          if(file.size >= imageType.size){
            showLocalNotification(this.$vs
              , this.$t('general.warning')
              , this.$t('course-outline.file-to-large')
              , notificationType.WARNING)

            if(type === 'vertical'){
              this.verticalFileUpload = null;
            }else{
              this.horizontalFileUpload = null;
            }
            return;
          }

          let reader = new FileReader();
          reader.readAsDataURL(file);
          const _this = this
          reader.onload =  async function(evt) {
            let img = new Image();
            img.onload = async () => {
              //check image resolution
              if(img.width !== imageType.width || img.height !== imageType.height) {
                showLocalNotification(_this.$vs
                  , _this.$t('general.warning')
                  , _this.$t('general.wrong-image-resolution')
                  , notificationType.WARNING)
                file = null;
                return;
              }

              if(type === 'vertical'){
                _this.verticalFileUpload = file;
              }else{
                _this.horizontalFileUpload = file;
              }

              //do upload

              let uploadPayload = {
                file: file,
                code: _this.courseOutline.subject.code,
                operationName: config.operationName.courseOutline,
              }
              let resultUpload = await _this.uploadAvatar(uploadPayload);

              if (resultUpload) {
                //save course outline picture

                let payload;
                if(type === 'vertical'){
                  _this.courseOutline.verticalPicturePath = resultUpload.file.uuid;
                  payload = {
                    id: _this.courseOutline.id,
                    courseOutlineInput: {
                      verticalPicturePath: _this.courseOutline.verticalPicturePath,
                    }
                  }
                } else {
                  _this.courseOutline.picturePath = resultUpload.file.uuid;
                  payload = {
                    id: _this.courseOutline.id,
                    courseOutlineInput: {
                      picturePath: _this.courseOutline.picturePath,
                    }
                  }
                }
                await _this.updateCourseOutline(payload);
              }

            }
            img.src = evt.target.result;
          }

        } else {
          if(type === 'vertical'){
            this.verticalFileUpload = null;
          }else{
            this.horizontalFileUpload = null;
          }
        }
      }
    },
  }
</script>

<style scoped>

</style>
